import {Component, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleXmark,
  faClipboardCheck,
  faClipboardList
} from "@fortawesome/pro-solid-svg-icons";
import {CircleState} from "../../../util/design/circle-state/circle-state.component";
import {ProjectFile, QualityCheck, QualityCheckProjectFiles, QualityControllerService, Task} from "../../../../api";
import {TaskDetailsService} from "../../../../services/tasks/task-details.service";
import {Check, QualityService, Rule} from "../../../../quality-api";
import {ActivatedRoute, Router} from "@angular/router";
import {StateConfigService} from "../../../../services/state/state-config.service";
import {HttpClient} from "@angular/common/http";

import {OverviewReportComponent} from "../../overviewReport/overview-report.component";
import {QualityStepperComponent} from "../quality-stepper/quality-stepper.component";
import {
  BicResultColumnComponent,
  BicResultComponent,
  BicResultHeaderComponent,
  BicResultRowComponent
} from "@buildinginformationcloud/bic-ui-components";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {roundPercentPreservingExtremes} from '../../../../models/report/report-leaves';
import {StatusSummary} from '../../../../models/report/normalized-report.model';
import {qualityCheckedRuleCount, qualityStatusSummary, statusForQualityResults} from './quality-report-summary';
import LphEnum = Task.LphEnum;

export function stateToCircleState(state: string): CircleState {
  switch (state) {
    case 'success':
      return CircleState.GREEN;
    case 'error':
      return CircleState.RED;
    case 'warning':
      return CircleState.YELLOW;
    default:
      return CircleState.GREEN;
  }

}

export function statusForProjectFile(file: QualityCheckFile | QualityCheckProjectFiles) {
  // @ts-ignore
  return statusForQualityResults(Object.values(file.check!.result).flatMap(result => result))
}

export interface QualityCheckFile {
  projectFile: ProjectFile;
  check: Check;
}

@Component({
  selector: 'app-quality-reports',
  templateUrl: './quality-report.component.html',
  styleUrls: ['./quality-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    OverviewReportComponent,
    QualityStepperComponent,
    BicResultColumnComponent,
    BicResultRowComponent,
    BicResultComponent,
    BicResultHeaderComponent,
    FaIconComponent,
  ]
})
export class QualityReportComponent implements OnInit {
  private taskDetailService = inject(TaskDetailsService);
  private qualityService = inject(QualityService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private stateConfigService = inject(StateConfigService);
  private httpClient = inject(HttpClient);
  private qualityControllerService = inject(QualityControllerService);

  private get apiBasePath(): string {
    return (this.qualityControllerService as any).basePath ?? 'http://localhost:8080';
  }


  qualityCheck: QualityCheck = {};
  loading = true;
  rules: Rule[] = [];
  @Input() lph!: LphEnum;

  selectedFile: QualityCheckProjectFiles | undefined;
  selectedRule: Rule | undefined;

  @Output() viewChange = new EventEmitter<boolean>();

  /** Overview-Kreise: je Datei und Prüfregel ein Eintrag (siehe {@link qualityStatusSummary}). */
  statusSummary: StatusSummary = {completed: 0, warning: 0, error: 0};
  /** Distinkt geprüfte Prüfregeln über alle Dateien. */
  checkedRuleCount = 0;

  navigateToRuleView(file: any) {
    this.router.navigate([file.id], {relativeTo: this.activatedRoute});
  }

  navigateToComplianceCheck(){
    this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'quality', this.qualityCheck.id, 'report', 'compliance']);
  }

  get rulesOfSelectedProjectFile(): Rule[] {
    if (!this.selectedFile) {
      return [];
    }
    return Object.keys(this.selectedFile.check!.result)
      .map(ruleId => this.rules
        .find(rule => rule.codeId === ruleId)!)
      .filter(rule => rule !== undefined) as Rule[]
  }

  protected readonly faCircleXmark = faCircleXmark;

  protected readonly statusForProjectFile = statusForProjectFile;

  statusForRuleAndSelectedFile(rule: Rule) {
    if (!this.selectedFile) {
      return undefined;
    }

    return statusForQualityResults(this.selectedFile.check!.result[rule.codeId])
  }

  resultPercentageForFile(file: QualityCheckProjectFiles): number {
    // @ts-ignore
    const result = Object.values(file.check!.result).flatMap(result => result).map(result => result.type)

    const totalResults = result.length;
    const successfulResults = result.filter((res: string) => res === 'success').length;

    return roundPercentPreservingExtremes((successfulResults / totalResults) * 100);
  }

  resultPercentageForRuleAndSelectedFile(rule: Rule): number {
    if (!this.selectedFile) {
      return 0;
    }

    const result = this.selectedFile.check!.result[rule.codeId].map((res: any) => res.type)

    const totalResults = result.length;
    const successfulResults = result.filter((res: string) => res === 'success').length;

    return roundPercentPreservingExtremes((successfulResults / totalResults) * 100);
  }


  protected readonly faClipboardList = faClipboardList;
  protected readonly CircleState = CircleState;
  protected readonly stateToCircleState = stateToCircleState;
  protected readonly faClipboardCheck = faClipboardCheck;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faSpinnerThird = faSpinnerThird;

  ngOnInit(): void {
    this.qualityCheck = this.activatedRoute.parent?.parent?.snapshot.data.qualityCheck;

    this.qualityService.rules().subscribe(res => {
      this.rules = res
      this.loading = false;
    })

    const files = this.qualityCheck?.qualityCheckProjectFiles ?? [];
    this.statusSummary = qualityStatusSummary(files);
    this.checkedRuleCount = qualityCheckedRuleCount(files);
  }

  get resultOfSelectedProjectFileAndSelectedRule(): any[] {
    if (!this.selectedFile || !this.selectedRule) {
      return [];
    }

    return this.selectedFile.check!.result[this.selectedRule.codeId]
  }

  createTaskForFile(file: QualityCheckProjectFiles) {
    let task: Task = {
      title: `Überarbeiten notwendig für: ${file.projectFile?.name ?? ''}`.trim(),
    }

    this.taskDetailService.setOpenedTask(task);
  }

  createTaskForRuleAndSelectedFile(rule: Rule) {
    if (!this.selectedFile) {
      return;
    }

    const fileName = this.selectedFile.projectFile?.name ?? '';
    let task: Task = {
      title: `Überarbeiten notwendig für: ${fileName}${fileName ? ' ' : ''}bezüglich Regel: ${rule.name}`,
    }

    this.taskDetailService.setOpenedTask(task);
  }

  protected downloadReport() {
    this.httpClient.get(`${this.apiBasePath}/quality/${encodeURIComponent(this.qualityCheck.id!)}/quality-report`, {
      params: {projectId: this.stateConfigService.getProjectId().toString()},
      responseType: 'blob'
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bericht_quality_check.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  }
}



